/**
 * Phone-number helpers (P4-T6). E.164 client gate mirrors the server's own
 * normalization + regex (`dial.dto.ts:28-38`, design audit #4) so pasted
 * national formats are fixed before submit, not rejected by a bare 400.
 */

import { mutationErrorMessage, normalizeApiError } from '@/features/common/apiError';

import type { FactoryWithForwarding } from './types';

/** Design audit #4: strip spaces, dashes, parentheses, dots; trim. */
export function normalizeE164(raw: string): string {
  return raw.trim().replace(/[\s()\-.]/g, '');
}

/** Server gate: leading `+`, first digit 1-9, 6-15 digits total. */
export function isValidE164(normalized: string): boolean {
  return /^\+[1-9]\d{5,14}$/.test(normalized);
}

/** Verbatim design §7 field error. */
export const E164_FORMAT_ERROR =
  'Enter the number in international format starting with +, e.g. +14155550123.';

/**
 * Bind-dialog grouping (design §9.1 / audit #5): the voice-assistant group is
 * computed from `forwarding.enabled` — never a name/kind hardcode (OQ-5 parks
 * exposing `kind` server-side). Both groups sort alphabetically by name.
 */
export function groupFactoriesByVoice(factories: FactoryWithForwarding[]): {
  voiceAssistants: FactoryWithForwarding[];
  others: FactoryWithForwarding[];
} {
  const byName = (a: FactoryWithForwarding, b: FactoryWithForwarding) =>
    a.name.localeCompare(b.name);
  const voiceAssistants: FactoryWithForwarding[] = [];
  const others: FactoryWithForwarding[] = [];
  for (const factory of factories) {
    if (factory.forwarding?.enabled === true) {
      voiceAssistants.push(factory);
    } else {
      others.push(factory);
    }
  }
  return { voiceAssistants: voiceAssistants.sort(byName), others: others.sort(byName) };
}

/** 12-char middle-truncation for id chips (design §6.2 webhook-key column). */
export function truncateId(id: string, max = 12): string {
  return id.length > max ? `${id.slice(0, max)}…` : id;
}

/**
 * Registry-surface error copy (design §13): the fixed 404 phrase for numbers,
 * the shared 403 phrase + server detail elsewhere (via `mutationErrorMessage`).
 */
export function numbersErrorMessage(error: unknown, fallback: string): string {
  if (normalizeApiError(error).status === 404) {
    return "Couldn't find that number.";
  }
  return mutationErrorMessage(error, fallback);
}
